import { AUTH_KEY, AUTH_TYPE_KEY } from '../constants/localStorageKeys'
import { removeFromStorage } from './storageUtils'

export const defaultContentType: string = 'application/json; charset=UTF-8'
export const defaultHeaders: Record<string, string> = {}

export function updateDefaultHeader(headerName: string, headerValue: string) {
  defaultHeaders[headerName] = headerValue
}

async function http<T>(path: string, config: RequestInit, contentType: string): Promise<T> {
  const fullConfig: RequestInit = { ...config, headers: { ...config.headers, ...defaultHeaders, 'Content-Type': contentType }, }
  const response = await fetch(path, fullConfig)

  if (!response.ok) {
    if (response.status === 403 || response.status === 401) {
      removeFromStorage(AUTH_KEY)
      removeFromStorage(AUTH_TYPE_KEY)
      window.location.href = '/login'
    }
    throw response
  }

  if (!response.headers.get('content-type')?.includes('application/json')) {
    return response.text() as any
  }

  return response.json().catch((e) => {
    throw new Error(`Failed to parse json in response from ${path} with status ${response.status}, ${response.statusText}: ${e}`)
  })
}

export async function get<TResponseBody>(path: string, config?: RequestInit): Promise<TResponseBody> {
  const getConfig = { method: 'GET', ...config, }
  return await http<TResponseBody>(path, getConfig, defaultContentType)
}

export async function mergePatch<TRequestBody, TResponseBody>(path: string, body: TRequestBody, config?: RequestInit): Promise<TResponseBody> {
  const patchConfig = { method: 'PATCH', body: JSON.stringify(body), ...config, }
  return await http<TResponseBody>(path, patchConfig, 'application/merge-patch+json; charset=UTF-8')
}

export async function post<TRequestBody, TResponseBody>(path: string, body: TRequestBody, config?: RequestInit): Promise<TResponseBody> {
  const postConfig = { method: 'POST', body: JSON.stringify(body), ...config, }
  return await http<TResponseBody>(path, postConfig, defaultContentType)
}

export async function put<TRequestBody, TResponseBody>(path: string, body: TRequestBody, config?: RequestInit): Promise<TResponseBody> {
  const putConfig = { method: 'PUT', body: JSON.stringify(body), ...config, }
  return await http<TResponseBody>(path, putConfig, defaultContentType)
}

export async function remove<TResponseBody>(path: string, config?: RequestInit): Promise<TResponseBody> {
  const deleteConfig = { method: 'DELETE', ...config }
  return await http<TResponseBody>(path, deleteConfig, defaultContentType)
}
